<template>
	<v-dialog v-model="dialog_open" max-width="800" persistent scrollable>
		<v-card>
			<v-card-title>
				<h3>Export content from this collection</h3>
			</v-card-title>
			<v-card-text class="mt-3" style="font-size:16px">
				Export this collection as a Thin Common Cartridge, which can then be imported into any learning object
				repository (LOR) or learning management system (LMS) that supports the 1EdTech Common Cartridge
				standard.
				<!-- <pre>{{ JSON.stringify(filtered_item_tree, false, 2) }}</pre> -->
				<div class="mt-2">
					<div>
						<v-checkbox class="mt-0 pt-0"
							:label="`Export ${site_config.sparkl_app_name} activities as LTI links`"
							v-model="lti_toggle" hide-details>
						</v-checkbox>
					</div>
				</div>
				<div class="mt-4">
					<span>Select items for export:</span>
					<v-treeview open-on-click dense :items="treeview_source" v-model="selected_items" selectable>
					</v-treeview>
				</div>
			</v-card-text>
			<v-card-actions class="pl-4 pr-4 pb-4">
				<v-btn color="secondary" @click="$emit('dialog_cancel')" class="mr-1">
					<v-icon small class="mr-2">fas fa-times</v-icon> Cancel</v-btn>
				<v-spacer />
				<v-btn v-if="show_case_export_btn" color="#666" text @click="export_case" class="k-tight-btn mr-2">Export CASE Resource Package (BETA)</v-btn>
				<v-spacer />
				<v-btn color="primary" @click="export_cartridge" class="k-tight-btn">
					<v-icon small class="mr-2">fas fa-check</v-icon> Export Cartridge</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import JSZip from 'jszip'
import { create } from 'xmlbuilder2'
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		collection: { type: Object, required: true }
	},
	data() {
		return {
			dialog_open: true,
			lti_toggle: false,
			manifest_uuid: U.new_uuid(),
			language: 'en-US',
			selected_items: [],
		}
	},
	computed: {
		...mapState(['site_config']),
		...mapGetters([]),

		show_case_export_btn() { return vapp.has_admin_right('su') },

		manifest_metadata() {
			return {
				'schema': 'IMS Thin Common Cartridge',
				'schemaversion': '1.3.0',
				'lomm:lom': {
					'lomm:general': {
						'lomm:identifier': {
							'lomm:catalog': 'GUID',
							'lomm:entry': this.manifest_uuid,
						},
						'lomm:title': {
							'lomm:string': {
								'@language': this.language,
								'#text': this.collection.title,
							},
						},
						'lomm:description': {
							'lomm:string': {
								'@language': this.language,
								'#text': this.collection.description,
							},
						},
					}
				}
			}
		},

		item_tree() {
			// First, establish 'root' item in organization. This represents the Collection as a whole
			const items = { "@identifier": "root", item: [] }

			// Next, we need to build a nested set of 'item' objects for each unit
			// An 'item' can be a folder, a subfolder, or a resource/lesson
			this.collection.units.forEach(unit => {
				const lookup = {}
				let resource_tree = unit.resource_tree
				let root

				// Step 1: If there are more than one folders defined, we need to
				// create a lookup object to build our nested set from.
				resource_tree.folders.forEach(folder => {
					const folder_identifier = folder.folder_id == "top" ? `top_${unit.lp_unit_id}` : `folder_${folder.folder_id}`
					const folder_title = folder.folder_id == "top" ? unit.title : folder.title
					lookup[folder.folder_id] = { "@identifier": folder_identifier, title: folder_title, item: [], seq: folder.seq }
				})

				// Step 2: Populate the item arrays and find the root folder
				resource_tree.folders.forEach(folder => {
					if (folder.parent_folder_id) {
						// This folder has a parent, so push it to the parent's item array
						lookup[folder.parent_folder_id].item.push(lookup[folder.folder_id])
					} else {
						// This folder has no parent, so it's the root of the tree
						root = lookup[folder.folder_id]
					}
				})

				// Step 3: We need a cleaned folder_assigments array, with all "phantom" resources ignored
				const valid_folder_assignments = resource_tree.folder_assignments.filter((folder_assignment) => {
					const matches_resource = unit.resources.some(resource => resource.resource_id === folder_assignment.resource_id)
					if (matches_resource) return true
					const matches_lesson = unit.lessons.some(lesson => lesson.lesson_id == folder_assignment.resource_id)
					if (matches_lesson) return true
					return false
				})

				// Step 4: Add files to the item arrays
				valid_folder_assignments.forEach(folder_assignment => {
					if (folder_assignment.parent_folder_id) {
						// This file has a parent folder, so push it to the parent's item array
						const is_lesson = folder_assignment.type == 'lesson'
						const item = is_lesson
							? unit.lessons.find(l => l.lesson_id == folder_assignment.resource_id)
							: unit.resources.find(r => r.resource_id == folder_assignment.resource_id)

						const resource_title = is_lesson
							? item?.lesson_title ?? "n/a"
							: item?.description ?? "n/a"

						// TODO: This might break if there was the same item in multiple folders
						// Perhaps need to concat folder id?
						const item_identifier = `item_${folder_assignment.resource_id}`
						const resource_identifier = `resource_${folder_assignment.resource_id}`

						lookup[folder_assignment.parent_folder_id].item.push({ "@identifier": item_identifier, "@identifierref": resource_identifier, title: resource_title, seq: folder_assignment.seq, item_o: item })
					}
				})

				// Step 5: Sort the item arrays based on the seq property, then delete that property as we don't want it in the xml
				for (const folder of Object.values(lookup)) {
					folder.item.sort((a, b) => a.seq - b.seq)
					folder.item.forEach(item => delete item.seq)
				}
				delete (root.seq)
				items.item.push(root)
			})
			return items
		},

		treeview_source() {
			// We need to transform the item_tree from a TCC friendly structure to a Vuetify treeview friendly structure
			const transform_node = (node) => {
				return {
					id: node['@identifier'],
					name: node['@identifier'] !== 'root' ? node.title : this.collection.title,
					children: (node.item || []).map(transform_node),
				}
			}
			return [transform_node(this.item_tree)]
		},

		selected_item_tree() {
			// Filter item tree based on treeview selections
			const filter_node = (node) => {
				const filtered_children = node.item ? node.item.map(filter_node).filter(Boolean) : []
				if (!this.selected_items.includes(node['@identifier']) && filtered_children.length === 0) return null
				return {
					'@identifier': node['@identifier'],
					'title': node.title,
					'item': filtered_children
				}
			}
			const selected_item_tree = filter_node(this.item_tree)
			if (!selected_item_tree) return { '@identifier': 'root', 'item': [] }
			return selected_item_tree
		},

		selected_resource_list() {
			// Filters resource_list based on selected_items
			return this.resource_list.filter(resource =>
				this.selected_items.includes(resource['@identifier'].replace('resource_', 'item_'))
			)
		},

		resource_list() {
			return this.collection.units.reduce((resources, unit) => {
				const unit_resources = unit.resources.map(resource => {
					const is_activity = resource.type === 'sparkl'
					const url = this.url_from_resource(resource)
					const use_lti_link = is_activity && this.lti_toggle
					// LTI links and Web Links have unique @type attributes
					const type_attribute = use_lti_link ? 'imsbasiclti_xmlv1p3' : 'imswl_xmlv1p3'
					let resource_object = {
						"@identifier": `resource_${resource.resource_id}`,
						"@type": type_attribute,
					}

					// Here we need to determine if the resource node gets a weblink or an lti link, and append to the resource object accordingly
					if (use_lti_link) {
						resource_object["lticc:cartridge_basiclti_link"] = {
							"blti:title": "Velocity",
							"blti:description": resource.description,
							"blti:launch_url": url
							// Question: Do we need to include blti:vendor here?
						}
					} else {
						resource_object["wl:webLink"] = {
							"wl:title": resource.description,
							"wl:url": {
								"@href": url,
							},
						}
					}
					return resource_object
				})

				const unit_lessons = unit.lessons.map(lesson => ({
					"@identifier": `resource_${lesson.lesson_id}`,
					"@type": "imswl_xmlv1p3",
					"wl:webLink": {
						"wl:title": lesson.lesson_title,
						"wl:url": {
							"@href": this.url_from_lesson(lesson),
						},
					},
				}))

				return resources.concat(unit_resources, unit_lessons)
			}, [])
		},

		manifest_organization() {
			// The <organizations> node of TCC contains the hierarchy of all folders/subfolders and resources
			const organization = {
				'organization': {
					'@identifier': 'TOC',
					'@structure': 'rooted-hierarchy',
					'item': this.selected_item_tree,
				}
			}

			return organization
		},

		manifest() {
			return {
				manifest: {
					// manifest root attributes
					'@identifier': `manifest_${this.manifest_uuid}`,
					'@xmlns': 'http://www.imsglobal.org/xsd/imsccv1p3/imscp_v1p1',
					'@xmlns:lomr': 'http://ltsc.ieee.org/xsd/imsccv1p3/LOM/resource',
					'@xmlns:lomm': 'http://ltsc.ieee.org/xsd/imsccv1p3/LOM/manifest',
					'@xmlns:wl': 'http://www.imsglobal.org/xsd/imsccv1p3/imswl_v1p3',
					'@xmlns:lticc': 'http://www.imsglobal.org/xsd/imslticc_v1p3',
					'@xmlns:blti': 'http://www.imsglobal.org/xsd/imsbasiclti_v1p0',
					'@xmlns:xsi': 'http://www.w3.org/2001/XMLSchema-instance',
					'@xsi:schemaLocation': "http://www.imsglobal.org/xsd/imsccv1p3/imscp_v1p1 http://www.imsglobal.org/profile/cc/ccv1p3/ccv1p3_imscp_v1p2_v1p0.xsd http://ltsc.ieee.org/xsd/imsccv1p3/LOM/resource http://www.imsglobal.org/profile/cc/ccv1p3/LOM/ccv1p3_lomresource_v1p0.xsd http://ltsc.ieee.org/xsd/imsccv1p3/LOM/manifest http://www.imsglobal.org/profile/cc/ccv1p3/LOM/ccv1p3_lommanifest_v1p0.xsd http://www.imsglobal.org/xsd/imsccv1p3/imscsmd_v1p0 http://www.imsglobal.org/profile/cc/ccv1p3/ccv1p3_imscsmd_v1p0.xsd http://www.imsglobal.org/xsd/imsccv1p3/imswl_v1p3 http://www.imsglobal.org/profile/cc/ccv1p3/ccv1p3_imswl_v1p3.xsd http://www.imsglobal.org/xsd/imslticc_v1p3 http://www.imsglobal.org/xsd/lti/ltiv1p3/imslticc_v1p3.xsd http://www.imsglobal.org/xsd/imsbasiclti_v1p0 http://www.imsglobal.org/xsd/lti/ltiv1p0/imsbasiclti_v1p0p1.xsd",
					// manifest nodes
					'metadata': this.manifest_metadata,
					'organizations': this.manifest_organization,
					'resources': {
						'resource': this.selected_resource_list
					}
				}
			}
		},
		thin_common_cartridge_xml() {
			return create(this.manifest, { encoding: 'UTF-8' }).end({ pretty: true })
		},

		satchel_origin() {
			return this.site_config.satchel_origin
		},
	},
	mounted() {
		vapp.cc_export_component = this

		this.selected_items = this.treeview_source.map(item => item.id)
	},
	methods: {
		url_from_lesson(lesson) {
			return `${window.location.origin}/lesson/${lesson.lesson_id}`
		},
		url_from_resource(resource) {
			const url = resource.url
			switch (resource.type) {
				case "website":
				case "video":
				case "equella":
				case "interactive":
				case "document":
					return url
				case "upload":
				case "html":
					// Adapted from full_url method in resources.js
					if (url.includes("https:")) return url
					return `${window.location.origin}/user-files/${url}`
				case "sparkl":
					return `${this.site_config.sparkl_origin}/${url}`
				default:
					return url
			}
		},
		export_cartridge() {
			const zip = new JSZip()
			zip.file("imsmanifest.xml", this.thin_common_cartridge_xml)

			zip.generateAsync({ type: "blob" })
				.then(blob => {
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = 'manifest.imscc'
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					this.$emit('dialog_cancel')
				})
		},

		/////////////////////////////////////////////////////////////
		// CASE export (work in progress...)
		async get_sparkl_content(sparkl_activity_id, content_transformation = 'remove_punctuation') {
			// param content_transformation: can be one of:
			// - 'none': send raw content, including html tags
			// - 'remove_html': strip html tags
			// - 'remove_punctuation' (default): strip tags, remove almost all punctuation, and make all text lower-case
			return new Promise((resolve, reject) => {
				let payload = {
					user_id: this.$store.state.user_info.user_id, 
					activity_ids: JSON.stringify([sparkl_activity_id]),
					content_transformation: content_transformation,
				}
				U.ajax('get_sparkl_content_for_case_export', payload, result=>{
					if (empty(result?.raw_text_content) || empty(result.raw_text_content[sparkl_activity_id])) {
						console.warn('ERROR RETRIEVING SPARKL CONTENT: ' + sparkl_activity_id)
						resolve('')
					} else {
						resolve(result.raw_text_content[sparkl_activity_id])
					}
				}, error=>{
					console.error('get_sparkl_content error', error)
					resolve('')
				})
			})
		},

		async get_lesson_content(lesson_id, content_transformation = 'remove_punctuation') {
			// param content_transformation: can be one of:
			// - 'none': send raw content, including html tags
			// - 'remove_html': strip html tags
			// - 'remove_punctuation' (default): strip tags, remove almost all punctuation, and make all text lower-case
			return new Promise((resolve, reject) => {
				let payload = {
					user_id: this.$store.state.user_info.user_id, 
					lesson_id: lesson_id,
					content_transformation: content_transformation,
				}
				U.ajax('get_lesson_content_for_case_export', payload, result=>{
					if (empty(result?.raw_text_content)) {
						console.warn('ERROR RETRIEVING LESSON CONTENT: ' + lesson_id)
						resolve('')
					} else {
						resolve(result.raw_text_content)
					}
				}, error=>{
					console.error('get_lesson_content_for_case_export error', error)
					resolve('')
				})
			})
		},

		async add_case_item(framework_json, parent, item_data) {
			// for now, manually change this to true to include full html in the payload
			let include_full_html = false

			let item_identifier = this.case_identifier(item_data.identifier, `${framework_json.CFDocument.identifier}-asset-`)

			// add CFItem
			let cfitem = {
				identifier: item_identifier,
				uri: `${this.satchel_origin}/ims/case/v1p1/CFItems/${item_identifier}`,
				fullStatement: item_data.fullStatement,
				lastChangeDateTime: item_data.lc_date
			}
			if (!empty(item_data.humanCodingScheme)) cfitem.humanCodingScheme = item_data.humanCodingScheme

			/////////////////////////////////////////////////
			// for item_type 'item' (no, this nomenclature is not ideal)...
			if (item_data.item_type == 'item') {
				// assign itemType 'Learning Resource'
				let CFItemType = framework_json.CFDefinitions.CFItemTypes[0]
				cfitem.CFItemTypeURI = {
					title: CFItemType.title,
					identifier: CFItemType.identifier,
					uri: CFItemType.uri,
				}
				cfitem.CFItemType = CFItemType.title

				// we should have an `item_o` param that points to the resource or lesson
				let asset = item_data.item_o
				// construct extensions data from the item_o param that we should have
				if (empty(asset)) {
					console.warn('no item_o for item', item_data)
				} else {
					// /////////////////// LESSONS
					if (!empty(asset.lesson_id)) {
						// console.log('lesson ', object_copy(asset))
						// if (cfitem.fullStatement == 'Animal Survival') {
						// 	debugger
						// }
						cfitem.extensions = {
							ocxType: "Lesson Plan",
							// resourceURL: asset.standalone_link(),
							resourceURL: `https://alabamalearningexchange.org/lesson/${asset.lesson_id}`,
							content_source: 'Alabama State Department of Education',
							description: asset.description_for_case_resource()
						}
						// load the full lesson content
						// if (!this.loaded_lesson) cfitem.extensions.fullContent = await this.get_lesson_content(asset.lesson_id, 'remove_html')
						// this.loaded_lesson = true
						cfitem.extensions.fullContent = asset.full_text_for_case_resource()

					// ////////////////// SPARKL ACTIVITIES
					} else if (asset.type == 'sparkl') {
						// get text of sparkl activity via service from asset_search_data table
						let full_text_content = await this.get_sparkl_content(asset.url, 'remove_html')

						// optional: get full html
						let full_html_content 
						if (include_full_html) full_html_content = await this.get_sparkl_content(asset.url, 'none')

						// estimate time required based on length of full_text_content
						let est_time_required = Math.ceil(
							(full_text_content.length / 7)	// seconds
							/ 60							// minutes
						)
						est_time_required = `P${est_time_required}M`

						// for now hard-code gadoe as provider
						let provider = "Georgia Department of Education"

						// console.warn(`full_text_content (${full_text_content.length} / ${est_time_required}): ${item_data.fullStatement}`, full_text_content)
						// console.warn(`full_html_content: ${item_data.fullStatement}`, full_html_content)

						cfitem.extensions = {
							ocxType: "Activity",
							educationalUse: "assignment",
							learningResourceType: "interactive",	// was "resourceType"
							timeRequired: est_time_required,
							description: "Interactive textbook section",
							deliveryMechanism: "lti",
							deliverySystem: "sparkl",
							resourceURL: `${this.site_config.sparkl_origin}/${asset.url}`,
							isIframeable: true,
							provider: provider,
							fullContent: full_text_content,
						}
						if (include_full_html) cfitem.extensions.fullHTMLContent = full_html_content
					
					// ///////////////////// OTHER RESOURCES
					} else {
						cfitem.extensions = {
							ocxType: "Resource",
							resourceURL: asset.full_url(),
						}
						if (!empty(asset.long_description)) cfitem.extensions.description = U.html_to_text(asset.long_description)
						if (!empty(asset.extensions?.content_source)) cfitem.extensions.provider = asset.extensions?.content_source
					}

					// if we have alignments, save them as associations
					if (asset.standards?.length > 0) {
						for (let si = 0; si < asset.standards.length; ++si) {
							let standard = asset.standards[si]

							// note that the origin is the asset, and the destination is the aligned standard

							// use the satchel convention of adding the framework_identifier to destinationNodeURI.title like so:
							let dest_title = (standard.humanCodingScheme ? `${standard.humanCodingScheme} ` : '') + standard.fullStatement.substr(0, 30).trim()
							dest_title = `${dest_title} (:${standard.framework_identifier}:)`

							let assoc_guid = U.new_uuid()
							framework_json.CFAssociations.push({
								identifier: assoc_guid,
								uri: `${this.satchel_origin}/ims/case/v1p1/CFAssociations/${assoc_guid}`,
								associationType: "ext:isAlignedTo",
								originNodeURI: {
									title: cfitem.fullStatement.substr(0, 20).trim(),
									identifier: cfitem.identifier,
									uri: cfitem.uri
								},
								destinationNodeURI: {
									title: dest_title,
									identifier: standard.identifier,
									uri: `${this.satchel_origin}/ims/case/v1p1/CFItems/${standard.identifier}`,
								},
								lastChangeDateTime: item_data.lc_date
							})
						}
					}
				}
			}
			/////////////////////////////////////////////////

			framework_json.CFItems.push(cfitem)

			// add CFAssociation
			let assoc_guid = U.new_uuid()
			framework_json.CFAssociations.push({
				identifier: assoc_guid,
				uri: `${this.satchel_origin}/ims/case/v1p1/CFAssociations/${assoc_guid}`,
				associationType: "isChildOf",
				originNodeURI: {
					title: cfitem.fullStatement.substr(0, 20).trim(),
					identifier: cfitem.identifier,
					uri: cfitem.uri
				},
				destinationNodeURI: {
					title: (parent.title ? parent.title.substr(0, 20).trim() : parent.fullStatement.substr(0, 20).trim()),
					identifier: parent.identifier,
					uri: parent.uri
				},
				sequenceNumber: item_data.sequence,
				lastChangeDateTime: item_data.lc_date
			})

			// if we have children, recursively process them
			if (!empty(item_data.children)) {
				for (let ci = 0; ci < item_data.children.length; ++ci) {
					let child = item_data.children[ci]
					let arr = child['@identifier'].match(/(.*?)_(.*)/)
					if (!arr || arr.length != 3) {
						console.warn('bad child', child)
						continue
					}

					let child_item_data = {
						identifier: arr[2],
						fullStatement: child.title.trim(),
						sequence: ci+1,
						item_o: child.item_o,
						item_type: arr[1],
						// (note: the selected_item_tree structure holds children in the `item` field)
						children: child.item,
						lc_date: item_data.lc_date
					}
					await this.add_case_item(framework_json, cfitem, child_item_data)
					// break	// break to only do a single child
				}
			}
		},

		async export_case() {
			// console.log(this.item_tree)
			// console.log(this.resource_list)
			// console.log(this.selected_item_tree)

			// normally we don't load the full lesson content for all lessons until we need it; do this to load all lesson content now so we can generate the full content of each one
			await this.$store.dispatch('get_learning_progression', {
				course_code: this.collection.course_code,
				retrieve_full_lessons: true,
				force_reload: true,
			})

			// create a GUID for the export based on the collection title
			// TODO: is there a better way to do this??
			let framework_identifier = U.new_uuid_from_string(this.collection.title)

			U.loading_start()

			let lc_date = new Date().toISOString()

			let framework_json = {
				"CFDocument": {
					"identifier": framework_identifier,
					"uri": `${this.satchel_origin}/ims/case/v1p1/CFDocuments/${framework_identifier}`,
					"creator": "Common Good Learning Tools",
					"Publisher": "Georgia Department of Education",
					"title": this.collection.title,
					"lastChangeDateTime": lc_date,
					"language": "en",
					"adoptionStatus": "Draft",
					"frameworkType": "Resource Collection"
				},
				"CFItems": [],
				"CFAssociations": [],
				"CFDefinitions": {
					"CFConcepts": [],
					"CFSubjects": [],
					"CFLicenses": [],
					"CFItemTypes": [
						{
							"identifier": "75c4d14b-c4a1-4c78-a5ce-790124a0926b",
							"uri": `${this.satchel_origin}/ims/case/v1p1/CFItemTypes/75c4d14b-c4a1-4c78-a5ce-790124a0926b`,
							"title": "Learning Resource",
							"description": "Learning Resource",
							"typeCode": "Learning Resource",
							"hierarchyCode": "1",
							"lastChangeDateTime": "2025-04-14T00:00:00+00:00"
						}
					],
					"CFAssociationGroupings": []
				},
				"CFRubrics": [],
			}

			// only allowing for selecting at the unit level at this time
			// top-level items are units
			let parent = framework_json.CFDocument
			// for (let ui = 0; ui < this.item_tree.item.length; ++ui) {
			for (let ui = 0; ui < this.selected_item_tree.item.length; ++ui) {
				let unit = this.item_tree.item.find(x=>x['@identifier'] == this.selected_item_tree.item[ui]['@identifier'])

				// lp_unit_ids are all 4-digit integers; create a guid for it
				let lp_unit_id = unit['@identifier'].replace(/top_/, '')
				let unit_identifier = this.case_identifier(lp_unit_id, `${framework_identifier}-unit-`)
				// console.log(`${unit_identifier}: ${unit.title}`)

				// recursively add the unit, its folders, and its resources
				let item_data = {
					identifier: unit_identifier,
					fullStatement: unit.title.trim(),
					sequence: ui+1,
					item_o: null,
					item_type: 'unit',
					// (note: the item_tree structure holds children in the `item` field)
					children: unit.item,
					lc_date: lc_date
				}
				await this.add_case_item(framework_json, parent, item_data)
			}

			console.log(framework_json)

			U.loading_stop()
			// return

			let filename = this.collection.title
			U.download_json_file(framework_json, filename)
		},

		// from the given id, which may be an existing guid (for a resource) OR an integer (for a lesson), generate a "predictable random" case identifier
		case_identifier(id, base_string) {
			if (U.is_uuid(id)) return id
			return U.new_uuid_from_string(base_string + id)
		},
	},
}
</script>

<styles lang="scss"></styles>
